import { createSlice } from '@reduxjs/toolkit';
import { validateModel, validateFormField } from '../validation/personalDetails';
import { canProceed } from '../utils/validation/canProceed';
import moment from 'moment';
const initialState = {
  validation: {
    name: {}
  },
  hasTriedToSave: false,
  isDirty: false,
  isSaving: false,
  isLoading: false,
  canContinue: false,
  model: {
    name: {
      title: '',
      firstName: '',
      middleName: '',
      lastName: ''
    },
    birthDate: '',
    gender: '',
    maritalStatus: '',
    numberOfDependants: '',
    anotherCountry: '',
    otherCountry: '',
    UKCitizenship: '',
    emailAddress: '',
    mobileNumber: ''
  }
};

const personalDetails = createSlice({
  name: 'personalDetails',
  initialState,
  reducers: {
    loadPersonalDetails: (state) => {
      state.isLoading = true;
    },
    loadPersonalDetailsCompleted: (state, action) => {
      state.model = action.payload;
      state.isLoading = false;
    },
    loadPersonalDetailsRejected: (state) => {
      state.isLoading = false;
    },
    modelChange: (state, action) => {
      state.model = action.payload;
      state.isDirty = true;
    },
    fieldChange: (state, { payload: { field, value, section } }) => {
      if (section) {
        state.model[section][field] = value;
      } else {
        state.model[field] = value;
      }
      state.isDirty = true;
    },
    validate: (state, action) => {
      state.validation = validateModel(action.payload);
      state.canContinue = canProceed(state.validation);
    },
    validateForReview: (state, action) => {
      state.validation = validateModel(action.payload);
    },
    validateField: (state, action) => {
      const { field, value, section } = action.payload;
      if (section) {
        state.validation[section][field] = validateFormField(field, value, section);
      } else {
        state.validation[field] = validateFormField(field, value);
      }
      state.canContinue = canProceed(state.validation);
    },
    resetValidation: (state) => {
      state.validation = initialState.validation;
      state.canContinue = false;
    },
    save: (state) => {
      state.isSaving = true;
      state.hasTriedToSave = true;
    },
    saveCompleted: (state) => {
      state.isSaving = false;
    },
    saveCancelled: (state) => {
      state.isSaving = false;
    },
    saveRejected: (state) => {
      state.isSaving = false;
    },
    resetState: () => initialState,
    updatePersonalDetails: (state, action) => {
      const {
        personal_details,
        personal_details: { number_of_dependents },
        financial_details
      } = action.payload;

      const numberOfDependants =
        number_of_dependents && !isNaN(number_of_dependents)
          ? `${parseInt(number_of_dependents)}`
          : number_of_dependents || '0';
      const [otherCountry, anotherCountry] = financial_details.tax.countries_of_citizenship;
      state.model = {
        ...state.model,
        name: {
          ...(personal_details.name
            ? {
                title: personal_details.name.title || '',
                firstName: personal_details.name.first_name,
                middleName: personal_details.name.middle_name || '',
                lastName: personal_details.name.last_name
              }
            : state.model.name)
        },
        birthDate:
          (personal_details.dob &&
            moment(personal_details.dob, 'YYYYMMDD', true).format('YYYY-MM-DD')) ||
          state.model.birthDate,
        gender: personal_details.sex || state.model.gender,
        maritalStatus: personal_details.marital_status || state.model.maritalStatus,
        numberOfDependants: numberOfDependants || state.model.numberOfDependants,
        UKCitizenship: financial_details.tax.uk_citizen || state.model.UKCitizenship,
        anotherCountry: anotherCountry || state.model.anotherCountry,
        otherCountry: otherCountry || state.model.otherCountry
      };
    }
  }
});

export function tryToContinue(model, callback) {
  return (dispatch) => {
    dispatch(validate(model));
    dispatch(checkCanContinue(callback));
  };
}

function checkCanContinue(callback) {
  return (dispatch, getState) => {
    const { canContinue } = getState().personalDetails;
    if (canContinue) {
      if (typeof callback === 'function') {
        callback();
      }
      dispatch(resetValidation());
    }
  };
}

export function confirmChanges(editState, callback) {
  return (dispatch) => {
    dispatch(validate(editState));
    dispatch(confirmModelChanges(editState, callback));
  };
}

function confirmModelChanges(editState, callback) {
  return (dispatch, getState) => {
    const { canContinue } = getState().personalDetails;
    if (canContinue) {
      dispatch(modelChange(editState));
      if (typeof callback == 'function') {
        callback();
      }
    }
  };
}

export { personalDetails, validateModel };
export const {
  fieldChange,
  updatePersonalDetails,
  loadPersonalDetails,
  loadPersonalDetailsCompleted,
  loadPersonalDetailsRejected,
  modelChange,
  resetValidation,
  save,
  saveCancelled,
  saveCompleted,
  saveRejected,
  validate,
  validateForReview,
  validateField,
  resetState
} = personalDetails.actions;
